import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Container,
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BugReportIcon from "@mui/icons-material/BugReport";
import GroupsIcon from "@mui/icons-material/Groups";
import HandshakeIcon from "@mui/icons-material/Handshake";
import TerminalIcon from "@mui/icons-material/Terminal";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

import QrCode2Icon from "@mui/icons-material/QrCode2";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PaletteIcon from "@mui/icons-material/Palette";
import InsightsIcon from "@mui/icons-material/Insights";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { useHistory, Link as RouterLink } from "react-router-dom";
import logo from "../../logo/Logo.svg";
import "./landingPage.css";
import "aos/dist/aos.css";
import AOS from "aos";

const DEMO_EMAIL = "hello@betaappstore.com";

const NAV_LINKS = [
  { label: "Who it's for", href: "#audiences" },
  { label: "How it works", href: "#how" },
  { label: "Features", href: "#features" },
  { label: "Roadmap", href: "#roadmap" },
];

const AUDIENCES = [
  {
    icon: <BugReportIcon fontSize="large" />,
    title: "Internal QA teams",
    pitch:
      "Stop waiting on TestFlight. CI uploads on every merge, your testers install in seconds, regression runs start hours sooner.",
    bullets: [
      "CI-triggered uploads — Jenkins today, Fastlane / GitHub Actions on the roadmap",
      "Device-scoped access so the right build reaches the right phone",
      "Staging and production channels as first-class environments",
    ],
  },
  {
    icon: <GroupsIcon fontSize="large" />,
    title: "Business & stakeholder teams",
    pitch:
      "A clean portal for product managers, sales engineers, and leadership — no Xcode, no invite dance, no Play Console quirks.",
    bullets: [
      "Named, annotated builds with inline release notes",
      "Works on any phone the stakeholder already carries",
      "Install history per reviewer for demo-readiness audits",
    ],
  },
  {
    icon: <HandshakeIcon fontSize="large" />,
    title: "External clients & agencies",
    pitch:
      "A private preview channel for work you build for someone else — visible only to them, never to the public store.",
    bullets: [
      "Per-organisation isolation — zero cross-tenant leakage",
      "Explicit per-build access lists for sensitive previews",
      "White-label client portals (roadmap) for agency engagements",
    ],
  },
  {
    icon: <TerminalIcon fontSize="large" />,
    title: "Mobile dev & release engineers",
    pitch:
      "Replace the TestFlight / Play Internal Testing workflow with something you actually control, backed by a real API.",
    bullets: [
      "Multipart upload endpoint for IPA and APK — automate from any CI",
      "JWT-authenticated REST API across every resource",
      "Long-lived API tokens and webhooks on the roadmap",
    ],
  },
];

const FEATURES = [
  {
    icon: <ApartmentIcon />,
    title: "Multi-tenant organisations",
    body: "Users, apps and builds live inside organisations with an approval workflow and per-member roles.",
  },
  {
    icon: <PhoneIphoneIcon />,
    title: "iOS & Android builds",
    body: "Upload IPA or APK with version, environment and release notes. S3-backed storage with hashed, non-guessable keys.",
  },
  {
    icon: <LockPersonIcon />,
    title: "Fine-grained access control",
    body: "Grant access per app or per individual build. Perfect for private client previews and staged rollouts.",
  },
  {
    icon: <MarkEmailReadIcon />,
    title: "Email verification & reset",
    body: "Account lifecycle done right: verification codes, tokenised password resets, and configurable SMTP.",
  },
  {
    icon: <CloudUploadIcon />,
    title: "S3-backed storage",
    body: "Every artefact lives in your own AWS S3 bucket under a configurable prefix — no proprietary blob store.",
  },
  {
    icon: <AdminPanelSettingsIcon />,
    title: "Role-based admin console",
    body: "A dedicated React dashboard for super admins and org owners. JWT sessions, protected routes, audit-friendly.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Create your organisation",
    body: "Sign up, request an org, get approved. Invite your team and assign roles.",
    icon: <ApartmentIcon />,
  },
  {
    n: "02",
    title: "Upload a build",
    body: "Drop in an IPA or APK — or POST from CI on every green merge.",
    icon: <CloudUploadIcon />,
  },
  {
    n: "03",
    title: "Grant access",
    body: "Pick who sees each build. QA, a stakeholder, or a named client.",
    icon: <LockPersonIcon />,
  },
  {
    n: "04",
    title: "Tester installs",
    body: "Open the portal, tap install, start the session that matters.",
    icon: <PhoneIphoneIcon />,
  },
];

const ROADMAP = [
  {
    icon: <VerifiedUserIcon />,
    title: "Enterprise identity",
    body: "SSO via SAML 2.0 and OIDC, SCIM provisioning, MFA — for the IT review that gates every enterprise deal.",
  },
  {
    icon: <QrCode2Icon />,
    title: "Tester experience",
    body: "QR-code install flows, iOS OTA via manifest.plist, device registration, in-app feedback SDK.",
  },
  {
    icon: <AltRouteIcon />,
    title: "Release channels",
    body: "dev → alpha → beta → rc → production, with promotion approvals and automatic build expiry.",
  },
  {
    icon: <IntegrationInstructionsIcon />,
    title: "CI/CD integrations",
    body: "First-class Fastlane and GitHub Actions plugins, long-lived API tokens, webhooks for Slack, Teams, and Jira.",
  },
  {
    icon: <PaletteIcon />,
    title: "Client-facing white-label",
    body: "Per-organisation branding, custom domain, stripped-down install-only tester view for agency engagements.",
  },
  {
    icon: <InsightsIcon />,
    title: "Audit & analytics",
    body: "Complete audit log, configurable retention, install funnel metrics, build download leaderboards.",
  },
];

const STATS = [
  { value: 60, suffix: "s", label: "from CI green to tester's phone" },
  { value: 100, suffix: "%", label: "of builds auditable, per user" },
  { value: 2, suffix: "", label: "platforms — iOS and Android, one flow" },
  { value: 0, suffix: "", label: "TestFlight review waits ever" },
];

function smoothScrollTo(href) {
  if (!href?.startsWith("#")) return;
  const el = document.querySelector(href);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/* ---------- Inline SVGs ---------- */

function PhoneMockupSVG() {
  return (
    <svg
      viewBox="0 0 320 560"
      xmlns="http://www.w3.org/2000/svg"
      className="ba-phone-svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ba-phone-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#1a1a1a" />
          <stop offset="1" stopColor="#0a0a0a" />
        </linearGradient>
        <linearGradient id="ba-screen-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFF8E5" />
          <stop offset="1" stopColor="#FFFDF6" />
        </linearGradient>
      </defs>
      <rect
        x="8"
        y="8"
        width="304"
        height="544"
        rx="44"
        fill="url(#ba-phone-grad)"
        stroke="#262626"
        strokeWidth="2"
      />
      <rect
        x="20"
        y="20"
        width="280"
        height="520"
        rx="34"
        fill="url(#ba-screen-grad)"
      />
      <rect x="120" y="28" width="80" height="18" rx="9" fill="#0a0a0a" />
      <rect x="40" y="68" width="160" height="18" rx="4" fill="#0F172A" />
      <rect x="40" y="92" width="100" height="10" rx="3" fill="#94a3b8" />

      <g className="ba-build-row ba-build-row-1">
        <rect x="40" y="130" width="240" height="76" rx="14" fill="#fff" stroke="#F3E1B5" strokeWidth="1.2"/>
        <rect x="56" y="146" width="44" height="44" rx="10" fill="#FFF3CC" />
        <rect x="66" y="156" width="24" height="24" rx="4" fill="#F7B31D" />
        <rect x="112" y="148" width="120" height="12" rx="3" fill="#0F172A" />
        <rect x="112" y="168" width="80" height="8" rx="3" fill="#94a3b8" />
        <rect x="112" y="184" width="50" height="8" rx="4" fill="#10b981" />
      </g>
      <g className="ba-build-row ba-build-row-2">
        <rect x="40" y="218" width="240" height="76" rx="14" fill="#fff" stroke="#F3E1B5" strokeWidth="1.2"/>
        <rect x="56" y="234" width="44" height="44" rx="10" fill="#FFF3CC" />
        <rect x="66" y="244" width="24" height="24" rx="4" fill="#F7B31D" />
        <rect x="112" y="236" width="100" height="12" rx="3" fill="#0F172A" />
        <rect x="112" y="256" width="90" height="8" rx="3" fill="#94a3b8" />
        <rect x="112" y="272" width="38" height="8" rx="4" fill="#F7B31D" />
      </g>
      <g className="ba-build-row ba-build-row-3">
        <rect x="40" y="306" width="240" height="76" rx="14" fill="#fff" stroke="#F3E1B5" strokeWidth="1.2"/>
        <rect x="56" y="322" width="44" height="44" rx="10" fill="#FFF3CC" />
        <rect x="66" y="332" width="24" height="24" rx="4" fill="#F7B31D" />
        <rect x="112" y="324" width="90" height="12" rx="3" fill="#0F172A" />
        <rect x="112" y="344" width="110" height="8" rx="3" fill="#94a3b8" />
        <rect x="112" y="360" width="44" height="8" rx="4" fill="#64748b" />
      </g>

      <rect
        x="40"
        y="460"
        width="240"
        height="48"
        rx="12"
        fill="#F7B31D"
        className="ba-phone-cta"
      />
      <text
        x="160"
        y="490"
        textAnchor="middle"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, sans-serif"
        fontSize="14"
        fontWeight="700"
        fill="#0F172A"
      >
        Install build
      </text>
    </svg>
  );
}

function FloatingShape({ cls }) {
  return <span className={`ba-floater ${cls}`} aria-hidden="true" />;
}

function WorkflowSVG() {
  return (
    <svg
      viewBox="0 0 1200 220"
      xmlns="http://www.w3.org/2000/svg"
      className="ba-workflow-svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ba-line-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#F7B31D" stopOpacity="0" />
          <stop offset="0.4" stopColor="#F7B31D" stopOpacity="1" />
          <stop offset="1" stopColor="#F7B31D" stopOpacity="0.15" />
        </linearGradient>
      </defs>
      <path
        className="ba-workflow-path"
        d="M 80 110 Q 300 40, 450 110 T 750 110 T 1120 110"
        fill="none"
        stroke="url(#ba-line-grad)"
        strokeWidth="3"
        strokeDasharray="6 8"
        strokeLinecap="round"
      />
      {[150, 450, 750, 1050].map((cx, i) => (
        <g key={cx}>
          <circle
            cx={cx}
            cy="110"
            r="22"
            fill="#fff"
            stroke="#F7B31D"
            strokeWidth="3"
            className={`ba-workflow-node ba-workflow-node-${i}`}
          />
          <text
            x={cx}
            y="116"
            textAnchor="middle"
            fontFamily="ui-monospace, Menlo, monospace"
            fontSize="12"
            fontWeight="700"
            fill="#8B5E00"
          >
            {`0${i + 1}`}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* ---------- Sub-components ---------- */

function CountUp({ target, suffix = "", className }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setValue(target);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const start = performance.now();
            const duration = 1200;
            const tick = (t) => {
              const p = Math.min(1, (t - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(Math.round(target * eased));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target]);

  return (
    <span ref={ref} className={className}>
      {value}
      {suffix}
    </span>
  );
}

/* ---------- Main component ---------- */

const LandingPage = () => {
  const history = useHistory();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 700, once: true, offset: 60, easing: "ease-out-cubic" });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      setScrolled(y > 10);
      const doc = document.documentElement;
      const max = (doc.scrollHeight || 0) - window.innerHeight;
      setScrollPct(max > 0 ? Math.min(100, (y / max) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSignIn = () => history.push("/login");
  const handleSignUp = () => history.push("/register");
  const handleDemo = () => {
    window.location.href = `mailto:${DEMO_EMAIL}?subject=Beta%20Appstore%20%E2%80%94%20demo%20request`;
  };

  const handleNavClick = (href) => {
    setMobileOpen(false);
    smoothScrollTo(href);
  };

  return (
    <div className="ba-landing">
      <div
        className="ba-scroll-progress"
        style={{ transform: `scaleX(${scrollPct / 100})` }}
        aria-hidden
      />

      <AppBar
        position="sticky"
        elevation={0}
        className={`ba-navbar ${scrolled ? "ba-navbar-scrolled" : ""}`}
        color="transparent"
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters className="ba-toolbar">
            <Box
              component={RouterLink}
              to="/landing"
              className="ba-brand"
              aria-label="Beta Appstore home"
            >
              <img src={logo} alt="Beta Appstore" height={34} />
            </Box>

            {!isMdDown && (
              <Stack direction="row" spacing={1} className="ba-nav-links">
                {NAV_LINKS.map((link) => (
                  <Button
                    key={link.href}
                    onClick={() => handleNavClick(link.href)}
                    className="ba-nav-link"
                  >
                    {link.label}
                  </Button>
                ))}
              </Stack>
            )}

            {!isMdDown ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  onClick={handleSignIn}
                  className="ba-btn-ghost"
                  variant="text"
                >
                  Sign in
                </Button>
                <Button
                  onClick={handleSignUp}
                  className="ba-btn-primary ba-shimmer"
                  variant="contained"
                  disableElevation
                >
                  Sign up free
                </Button>
              </Stack>
            ) : (
              <IconButton
                onClick={() => setMobileOpen(true)}
                aria-label="Open navigation"
                className="ba-hamburger"
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ className: "ba-mobile-drawer" }}
      >
        <Box className="ba-mobile-drawer-head">
          <img src={logo} alt="Beta Appstore" height={30} />
          <IconButton onClick={() => setMobileOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {NAV_LINKS.map((link) => (
            <ListItem key={link.href} disablePadding>
              <ListItemButton onClick={() => handleNavClick(link.href)}>
                <ListItemText primary={link.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Box className="ba-mobile-drawer-cta">
          <Button fullWidth variant="text" onClick={handleSignIn}>
            Sign in
          </Button>
          <Button
            fullWidth
            variant="contained"
            className="ba-btn-primary"
            disableElevation
            onClick={handleSignUp}
          >
            Sign up free
          </Button>
          <Button fullWidth variant="outlined" onClick={handleDemo}>
            Request a demo
          </Button>
        </Box>
      </Drawer>

      {/* ---------- Hero ---------- */}
      <section className="ba-hero">
        <div className="ba-hero-grid" aria-hidden />
        <FloatingShape cls="ba-floater-1" />
        <FloatingShape cls="ba-floater-2" />
        <FloatingShape cls="ba-floater-3" />

        <Container maxWidth="lg" className="ba-hero-inner">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="Private iOS & Android distribution"
                className="ba-eyebrow"
                size="small"
              />
              <Typography
                variant="h1"
                className="ba-hero-title"
                data-aos="fade-up"
              >
                Ship beta builds to testers in{" "}
                <span className="ba-gradient-text">minutes, not days</span>.
              </Typography>
              <Typography
                variant="h5"
                className="ba-hero-sub"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                Beta Appstore is a private, self-hosted distribution platform
                for iOS and Android apps — the internal alternative to
                TestFlight and Google Play Internal Testing, built for teams
                that need control over who gets which build, when, and how.
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                className="ba-hero-cta"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <Button
                  size="large"
                  variant="contained"
                  disableElevation
                  className="ba-btn-primary ba-shimmer"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleSignUp}
                >
                  Sign up free
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  className="ba-btn-secondary"
                  onClick={handleDemo}
                >
                  Request a demo
                </Button>
              </Stack>
              <Stack
                direction="row"
                spacing={3}
                className="ba-hero-trust"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <span>
                  <CheckCircleIcon fontSize="small" /> No TestFlight wait
                </span>
                <span>
                  <CheckCircleIcon fontSize="small" /> Per-build access control
                </span>
                <span>
                  <CheckCircleIcon fontSize="small" /> Self-hosted on your AWS
                </span>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box className="ba-phone-stage" data-aos="zoom-in" data-aos-delay="150">
                <div className="ba-phone-glow" aria-hidden />
                <div className="ba-phone-ring ba-phone-ring-1" aria-hidden />
                <div className="ba-phone-ring ba-phone-ring-2" aria-hidden />
                <PhoneMockupSVG />

                <div className="ba-badge ba-badge-1">
                  <RocketLaunchIcon fontSize="small" />
                  <span>Build uploaded</span>
                </div>
                <div className="ba-badge ba-badge-2">
                  <QrCode2Icon fontSize="small" />
                  <span>Scan to install</span>
                </div>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ---------- Marquee strip ---------- */}
      <section className="ba-strip">
        <div className="ba-strip-track">
          <div className="ba-strip-group">
            <span>iOS · TestFlight-free</span>
            <span>•</span>
            <span>Android · private APK</span>
            <span>•</span>
            <span>S3-backed</span>
            <span>•</span>
            <span>JWT REST API</span>
            <span>•</span>
            <span>Self-hosted</span>
            <span>•</span>
            <span>Multi-tenant</span>
            <span>•</span>
          </div>
          <div className="ba-strip-group" aria-hidden>
            <span>iOS · TestFlight-free</span>
            <span>•</span>
            <span>Android · private APK</span>
            <span>•</span>
            <span>S3-backed</span>
            <span>•</span>
            <span>JWT REST API</span>
            <span>•</span>
            <span>Self-hosted</span>
            <span>•</span>
            <span>Multi-tenant</span>
            <span>•</span>
          </div>
        </div>
      </section>

      {/* ---------- Audiences ---------- */}
      <section id="audiences" className="ba-section">
        <Container maxWidth="lg">
          <Box className="ba-section-head" data-aos="fade-up">
            <Typography variant="overline" className="ba-overline">
              Who it's for
            </Typography>
            <Typography variant="h2" className="ba-section-title">
              One platform, four kinds of user.
            </Typography>
            <Typography variant="body1" className="ba-section-sub">
              Beta Appstore replaces TestFlight-style distribution for teams
              that can't wait on Apple review cycles, don't want to juggle
              invites, and need real control over who sees each build.
            </Typography>
          </Box>

          <Grid container spacing={3} className="ba-audience-grid">
            {AUDIENCES.map((a, i) => (
              <Grid item xs={12} md={6} key={a.title}>
                <Card
                  className="ba-card ba-audience-card ba-tilt"
                  data-aos="fade-up"
                  data-aos-delay={i * 80}
                >
                  <CardContent>
                    <Box className="ba-card-icon">{a.icon}</Box>
                    <Typography variant="h5" className="ba-card-title">
                      {a.title}
                    </Typography>
                    <Typography variant="body1" className="ba-card-pitch">
                      {a.pitch}
                    </Typography>
                    <Stack component="ul" spacing={1} className="ba-card-list">
                      {a.bullets.map((b) => (
                        <li key={b}>
                          <CheckCircleIcon
                            fontSize="small"
                            className="ba-list-check"
                          />
                          <span>{b}</span>
                        </li>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* ---------- How it works ---------- */}
      <section id="how" className="ba-section ba-section-cream">
        <Container maxWidth="lg">
          <Box className="ba-section-head" data-aos="fade-up">
            <Typography variant="overline" className="ba-overline">
              How it works
            </Typography>
            <Typography variant="h2" className="ba-section-title">
              From IPA on a laptop to app on a tester's phone — four steps.
            </Typography>
          </Box>

          <Box className="ba-workflow-wrap" data-aos="fade-up">
            <WorkflowSVG />
          </Box>

          <Grid container spacing={3} className="ba-steps">
            {STEPS.map((s, i) => (
              <Grid item xs={12} sm={6} md={3} key={s.n}>
                <Box
                  className="ba-step"
                  data-aos="fade-up"
                  data-aos-delay={i * 120}
                >
                  <div className="ba-step-icon">{s.icon}</div>
                  <div className="ba-step-n">{s.n}</div>
                  <Typography variant="h6" className="ba-step-title">
                    {s.title}
                  </Typography>
                  <Typography variant="body2" className="ba-step-body">
                    {s.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* ---------- Stats ---------- */}
      <section className="ba-stats">
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {STATS.map((s, i) => (
              <Grid item xs={6} md={3} key={s.label}>
                <Box
                  className="ba-stat"
                  data-aos="fade-up"
                  data-aos-delay={i * 80}
                >
                  <div className="ba-stat-value">
                    <CountUp target={s.value} suffix={s.suffix} />
                  </div>
                  <div className="ba-stat-label">{s.label}</div>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* ---------- Features ---------- */}
      <section id="features" className="ba-section">
        <Container maxWidth="lg">
          <Box className="ba-section-head" data-aos="fade-up">
            <Typography variant="overline" className="ba-overline">
              Shipping today
            </Typography>
            <Typography variant="h2" className="ba-section-title">
              Everything you need for private beta distribution.
            </Typography>
            <Typography variant="body1" className="ba-section-sub">
              Built on Express, TypeORM, MySQL, and AWS S3, with a React admin
              dashboard. Authenticated via JWT. Every action is auditable
              through the REST API.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {FEATURES.map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={f.title}>
                <Box
                  className="ba-feature ba-tilt"
                  data-aos="fade-up"
                  data-aos-delay={i * 60}
                >
                  <Box className="ba-feature-icon">{f.icon}</Box>
                  <Typography variant="h6" className="ba-feature-title">
                    {f.title}
                  </Typography>
                  <Typography variant="body2" className="ba-feature-body">
                    {f.body}
                  </Typography>
                  <span className="ba-feature-shine" aria-hidden />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* ---------- Roadmap ---------- */}
      <section id="roadmap" className="ba-section ba-section-dark">
        <Container maxWidth="lg">
          <Box className="ba-section-head ba-section-head-dark" data-aos="fade-up">
            <Typography variant="overline" className="ba-overline ba-overline-dark">
              Coming next
            </Typography>
            <Typography variant="h2" className="ba-section-title ba-section-title-dark">
              Built for today, designed for enterprise tomorrow.
            </Typography>
            <Typography variant="body1" className="ba-section-sub ba-section-sub-dark">
              The next phase adds identity, compliance and tester-experience
              features large organisations evaluate first.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {ROADMAP.map((r, i) => (
              <Grid item xs={12} sm={6} md={4} key={r.title}>
                <Box
                  className="ba-roadmap-item"
                  data-aos="fade-up"
                  data-aos-delay={i * 60}
                >
                  <Box className="ba-roadmap-icon">{r.icon}</Box>
                  <Typography variant="h6" className="ba-roadmap-title">
                    {r.title}
                  </Typography>
                  <Typography variant="body2" className="ba-roadmap-body">
                    {r.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="ba-cta">
        <div className="ba-cta-mesh" aria-hidden />
        <Container maxWidth="md">
          <Box className="ba-cta-inner" data-aos="fade-up">
            <Typography variant="h3" className="ba-cta-title">
              Ready to make beta distribution painless?
            </Typography>
            <Typography variant="body1" className="ba-cta-sub">
              Create your organisation in under a minute, or get a guided walk
              through from the team.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              className="ba-cta-buttons"
            >
              <Button
                size="large"
                variant="contained"
                disableElevation
                className="ba-btn-primary-dark ba-shimmer"
                onClick={handleSignUp}
              >
                Sign up free
              </Button>
              <Button
                size="large"
                variant="outlined"
                className="ba-btn-secondary-on-dark"
                onClick={handleDemo}
              >
                Request a demo
              </Button>
            </Stack>
          </Box>
        </Container>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="ba-footer">
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box className="ba-brand ba-brand-footer">
                <img src={logo} alt="Beta Appstore" height={32} />
              </Box>
              <Typography variant="body2" className="ba-footer-tag">
                Private iOS & Android distribution for teams that need control
                over every install.
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" className="ba-footer-col-title">
                Product
              </Typography>
              <Button
                color="inherit"
                className="ba-footer-link"
                onClick={() => handleNavClick("#features")}
              >
                Features
              </Button>
              <Button
                color="inherit"
                className="ba-footer-link"
                onClick={() => handleNavClick("#how")}
              >
                How it works
              </Button>
              <Button
                color="inherit"
                className="ba-footer-link"
                onClick={() => handleNavClick("#roadmap")}
              >
                Roadmap
              </Button>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" className="ba-footer-col-title">
                Account
              </Typography>
              <Button color="inherit" className="ba-footer-link" onClick={handleSignIn}>
                Sign in
              </Button>
              <Button color="inherit" className="ba-footer-link" onClick={handleSignUp}>
                Sign up
              </Button>
              <Button color="inherit" className="ba-footer-link" onClick={handleDemo}>
                Request demo
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" className="ba-footer-col-title">
                Legal
              </Typography>
              <Button
                color="inherit"
                component={RouterLink}
                to="/privacy-policy"
                className="ba-footer-link"
              >
                Privacy policy
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/terms-of-use"
                className="ba-footer-link"
              >
                Terms of use
              </Button>
              <Typography variant="body2" className="ba-footer-contact">
                Contact: <a href={`mailto:${DEMO_EMAIL}`}>{DEMO_EMAIL}</a>
              </Typography>
            </Grid>
          </Grid>

          <Divider className="ba-footer-divider" />
          <Box className="ba-footer-bottom">
            <Typography variant="body2">
              © {new Date().getFullYear()} Beta Appstore. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </footer>
    </div>
  );
};

export default LandingPage;
